import { useCallback, useMemo, useState } from 'react';

export type OntologyMoreListProps = {
  parentId: string;
  items: { id: string; label: string }[];
  onSelect: (parentId: string, selectedIds: string[]) => void;
  onClose: () => void;
};

export function OntologyMoreList({ parentId, items, onSelect, onClose }: OntologyMoreListProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(c => c.label.toLowerCase().includes(q) || c.id.toLowerCase().includes(q));
  }, [items, search]);

  const toggle = useCallback((id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelected(prev => (prev.size === filtered.length ? new Set() : new Set(filtered.map(c => c.id))));
  }, [filtered]);

  const handleConfirm = useCallback(() => {
    onSelect(parentId, Array.from(selected));
    onClose();
  }, [parentId, selected, onSelect, onClose]);

  if (items.length === 0) return null;

  return (
    <div
      className="ontology-more-list"
      style={{
        position: 'absolute',
        top: 16,
        right: 16,
        zIndex: 10,
        background: '#fff',
        border: '1px solid #e0e8f0',
        borderRadius: 10,
        boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
        padding: 14,
        width: 320,
        maxHeight: 440,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: '#1a2e44' }}>Sub-diseases ({items.length})</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 16,
            color: '#888',
            lineHeight: 1,
            padding: '0 2px',
          }}
        >
          ×
        </button>
      </div>

      {/* Search */}
      <input
        type="search"
        placeholder="Search sub-diseases…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        autoFocus
        style={{
          width: '100%',
          padding: '7px 10px',
          border: '1px solid #c8d6e5',
          borderRadius: 7,
          fontSize: 13,
          boxSizing: 'border-box',
          outline: 'none',
        }}
        onFocus={e => (e.currentTarget.style.borderColor = '#4a90d9')}
        onBlur={e => (e.currentTarget.style.borderColor = '#c8d6e5')}
      />

      {/* Select all / count */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button
          type="button"
          onClick={selectAll}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 12,
            color: '#4a90d9',
            padding: 0,
            fontWeight: 500,
          }}
        >
          {selected.size === filtered.length && filtered.length > 0 ? 'Deselect all' : 'Select all'}
        </button>
        <span style={{ fontSize: 11, color: '#888' }}>
          {filtered.length === items.length
            ? `${items.length} items`
            : `${filtered.length} of ${items.length}`}
          {selected.size > 0 && ` · ${selected.size} selected`}
        </span>
      </div>

      {/* List */}
      <ul
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          overflow: 'auto',
          flex: 1,
          minHeight: 120,
        }}
      >
        {filtered.length === 0 ? (
          <li
            style={{
              padding: '20px 8px',
              textAlign: 'center',
              color: '#aaa',
              fontSize: 13,
            }}
          >
            No results for "{search}"
          </li>
        ) : (
          filtered.map(c => (
            <li
              key={c.id}
              style={{
                borderRadius: 5,
                background: selected.has(c.id) ? '#e8f2ff' : 'transparent',
              }}
            >
              <label
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 8px',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <input
                  type="checkbox"
                  checked={selected.has(c.id)}
                  onChange={() => toggle(c.id)}
                  style={{ margin: 0, flexShrink: 0 }}
                />
                <span style={{ fontSize: 13, color: '#1a2e44' }}>{c.label}</span>
              </label>
            </li>
          ))
        )}
      </ul>

      {/* Footer */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onClose}
          style={{
            padding: '6px 14px',
            border: '1px solid #c8d6e5',
            borderRadius: 7,
            background: '#fff',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={selected.size === 0}
          style={{
            padding: '6px 14px',
            border: 'none',
            borderRadius: 7,
            background: selected.size > 0 ? '#4a90d9' : '#c8d6e5',
            color: '#fff',
            fontSize: 13,
            fontWeight: 500,
            cursor: selected.size > 0 ? 'pointer' : 'not-allowed',
          }}
        >
          Add to tree ({selected.size})
        </button>
      </div>
    </div>
  );
}
